import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import {
  AdminListRequestOfBaseFilter,
  ListResponseOfAdminContentItem,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

export const ContentBlockList = () => {
  const { formatMessage: t } = useIntl();
  const {
    contentService,
    contentBlockCreateUrl,
    contentBlockEditUrl,
    contentBlockDetailsUrl,
  } = useGlobalContext();
  const navigate = useNavigate();
  const { organizationId } = contextStore.useState();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState(
    (s) => s.contentBlockSearchTerm || ""
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId]);

  useTitle(t({ id: "contentBlocks" }));

  const { data, isFetching, refetch } = useStrictQuery<ListResponseOfAdminContentItem>(
    [
      "contentService.getContentBlocks",
      organizationId,
      debouncedSearchTerm,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      contentService.getContentBlocks(
        AdminListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            searchText: debouncedSearchTerm,
          },
        })
      ),
    { keepPreviousData: true }
  );

  const handleDelete = async (id: string) => {
    if (window.confirm(t({ id: "confirmDeleteContentBlock" }))) {
      try {
        await contentService.deleteContent(id);
        refetch();
      } catch (error) {
        console.error("Error deleting content block:", error);
        alert(t({ id: "errorDeletingContentBlock" }));
      }
    }
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 250,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "value",
      align: "left",
      width: 400,
      type: "node",
      View: ({ value }) => (
        <div dangerouslySetInnerHTML={{ __html: value.value }} />
      ),
      label: t({ id: "previewLabel" }),
    },
    {
      accessor: "inheritedOrganizationName",
      align: "left",
      width: 250,
      type: "text",
      label: t({ id: "inheritedFrom" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(contentBlockDetailsUrl(id));
  };

  const contentBlocks = (data?.data ?? []) as any[];

  const contextMenuItems = [
    {
      key: "edit",
      content: (
        <>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t({ id: "editLabel" })}
        </>
      ),
      onClick: (e: any, id: string) => {
        navigate(contentBlockEditUrl(id));
      },
    },
    {
      key: "delete",
      content: (
        <>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t({ id: "deleteLabel" })}
        </>
      ),
      onClick: (e: any, id: string) => {
        handleDelete(id);
      },
    },
  ];

  return (
      <Container>
        <Box mb={3} textAlign="center">
          <Link
            to={contentBlockCreateUrl()}
            style={{ textDecoration: "none" }}
          >
            <Fab aria-label="add" color="primary">
              <AddIcon />
            </Fab>
          </Link>
        </Box>

        <Box mb={3}>
          <TextField
            fullWidth
            variant="outlined"
            value={searchTerm}
            placeholder={t({ id: "searchPlaceholder" })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      persistanceStore.update((s) => {
                        s.contentBlockSearchTerm = "";
                      })
                    }
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              persistanceStore.update((s) => {
                s.contentBlockSearchTerm = e.target.value;
              });
            }}
          />
        </Box>

        <LazyTable
          {...{
            rowCount: data?.totalRecords ?? 0,
            rows: contentBlocks,
            loading: isFetching,
            dense: true,
            title: t({ id: "contentBlocks" }),
            cells,
            page,
            setPage,
            order,
            setOrder,
            orderBy,
            setOrderBy,
            rowsPerPage,
            setRowsPerPage,
            onClick,
            contextMenuItems,
          }}
        />
      </Container>
  );
};
